import {
  Alert,
  Card,
  DatePicker,
  Empty,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { financeApi, type AccountType, type TrialBalanceRow } from '@/api/finance';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const TYPE_COLOR: Record<AccountType, string> = {
  ASSET: 'success',
  LIABILITY: 'warning',
  INCOME: 'processing',
  EXPENSE: 'error',
  EQUITY: 'purple',
};

export function FinanceTrialBalancePage() {
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());

  const { data, isFetching } = useQuery({
    queryKey: ['finance', 'trial-balance', asOf.format('YYYY-MM-DD')],
    queryFn: () => financeApi.trialBalance(asOf.format('YYYY-MM-DD')),
  });

  const totalDr = (data?.rows ?? []).reduce((acc, r) => acc + Number(r.totalDebit), 0);
  const totalCr = (data?.rows ?? []).reduce((acc, r) => acc + Number(r.totalCredit), 0);
  const diff = Math.abs(totalDr - totalCr);

  const columns: TableProps<TrialBalanceRow>['columns'] = [
    {
      title: 'Code',
      dataIndex: 'accountCode',
      width: 120,
      render: (c: string) => <Typography.Text code>{c}</Typography.Text>,
    },
    {
      title: 'Account',
      dataIndex: 'accountName',
      render: (n: string) => <Typography.Text strong>{n}</Typography.Text>,
    },
    {
      title: 'Type',
      dataIndex: 'accountType',
      width: 130,
      render: (t: AccountType) => <Tag color={TYPE_COLOR[t]}>{t}</Tag>,
    },
    {
      title: 'Total DR',
      dataIndex: 'totalDebit',
      width: 160,
      align: 'right',
      render: (a: string) => `PKR ${PKR(a)}`,
    },
    {
      title: 'Total CR',
      dataIndex: 'totalCredit',
      width: 160,
      align: 'right',
      render: (a: string) => `PKR ${PKR(a)}`,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      width: 160,
      align: 'right',
      render: (a: string) => (
        <Typography.Text strong style={{ color: Number(a) < 0 ? '#dc2626' : undefined }}>
          PKR {PKR(a)}
        </Typography.Text>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Trial balance"
        description="DR / CR totals and net balance per account, as of any date."
        count={data ? { value: data.rows.length, label: 'accounts' } : undefined}
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/trial-balance.xlsx',
              params: { asOf: asOf.format('YYYY-MM-DD') },
              fileName: `trial-balance-${asOf.format('YYYY-MM-DD')}.xlsx`,
              disabled: !data,
            }]}
          />
        }
      />

      <Space wrap size={12}>
        <DatePicker
          value={asOf}
          onChange={(d) => d && setAsOf(d)}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {data && data.rows.length > 0 && (
          <Alert
            type={diff < 0.005 ? 'success' : 'warning'}
            showIcon
            message={
              <Space>
                <span>Total DR PKR {PKR(totalDr)}</span>
                <span>·</span>
                <span>Total CR PKR {PKR(totalCr)}</span>
                <span>·</span>
                <span>{diff < 0.005 ? 'Balanced' : `Diff PKR ${PKR(diff)}`}</span>
              </Space>
            }
          />
        )}
      </Space>

      {data && data.rows.length === 0 && !isFetching ? (
        <Empty description="No ledger entries posted yet" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="accountId"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data?.rows ?? []}
            pagination={false}
          />
        </Card>
      )}
    </div>
  );
}
